import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataService } from '../../services/data.service';

/** How long to wait before another caller may retry after a failed load. */
const FAILURE_COOLDOWN_MS = 30_000;

export interface Favorite {
  _id?: string;
  id?: number;
  type: 'entity' | 'saved-search';
  /** Mongoose model name of the pinned entity TYPE, e.g. 'Item'. Null for saved searches. */
  refModel?: string | null;
  name?: string;
  savedQuery?: Record<string, any> | null;
  /** Route slug for the pinned type's list view, resolved server-side. */
  apiControllerRoute?: string | null;
  entityLabel?: string;
  isDangling?: boolean;
}

/**
 * Single source of truth for the current user's favorites.
 *
 * Every pin/unpin in the app goes through here and pushes the new list onto `favorites$`, so a
 * star toggled on an entity page and the dashboard widget cannot disagree — that is what makes
 * "pinning from anywhere is immediately reflected on the widget" hold without polling.
 */
@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private readonly favoritesSubject = new BehaviorSubject<Favorite[]>([]);
  readonly favorites$: Observable<Favorite[]> = this.favoritesSubject.asObservable();

  private loaded = false;
  private inFlight: Promise<Favorite[]> | null = null;
  /**
   * When the last attempt failed, when it may be retried.
   *
   * Without this a failed load was retried by every caller: one FavoriteToggleComponent is
   * created per grid row, so a single failing request became one failed request (and one console
   * error) per row — enough to stall a large table.
   */
  private retryAfter = 0;

  constructor(private dataService: DataService) {}

  get snapshot(): Favorite[] {
    return this.favoritesSubject.value;
  }

  /** Loads once per session unless `force` is set; concurrent callers share one request. */
  async load(force = false): Promise<Favorite[]> {
    if (this.loaded && !force) return this.snapshot;
    if (this.inFlight) return this.inFlight;
    if (!force && Date.now() < this.retryAfter) return this.snapshot;

    this.inFlight = (async () => {
      try {
        const result: any = await this.dataService.get('api/user-favorites');
        const favorites: Favorite[] = Array.isArray(result) ? result : [];
        this.favoritesSubject.next(favorites);
        this.loaded = true;
        this.retryAfter = 0;
        return favorites;
      } catch (error) {
        // Logged once per cooldown window, not once per component instance.
        this.retryAfter = Date.now() + FAILURE_COOLDOWN_MS;
        console.error('Failed to load favorites', error);
        return this.snapshot;
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  /** Matched on the route slug, which is what every caller already has. */
  isEntityPinned(entity: string): boolean {
    if (!entity) return false;
    return this.snapshot.some(
      (f) => f.type === 'entity' && f.apiControllerRoute === entity,
    );
  }

  findByEntity(entity: string): Favorite | undefined {
    return this.snapshot.find((f) => f.type === 'entity' && f.apiControllerRoute === entity);
  }

  /**
   * Pins an entity type by the route slug the caller navigates by ('items'); the server resolves
   * it to the mongoose model name.
   */
  async pinEntity(entity: string, name?: string): Promise<void> {
    await this.dataService.post('api/user-favorites', {
      type: 'entity',
      entity,
      ...(name ? { name } : {}),
    });
    await this.load(true);
  }

  async pinSavedSearch(name: string, savedQuery: Record<string, any>): Promise<void> {
    await this.dataService.post('api/user-favorites', {
      type: 'saved-search',
      name,
      savedQuery,
    });
    await this.load(true);
  }

  async unpinById(favoriteId: string): Promise<void> {
    await this.dataService.delete(`api/user-favorites/${favoriteId}`);
    await this.load(true);
  }

  /** Toggles an entity-type pin. Returns the resulting state. */
  async toggleEntity(entity: string, name?: string): Promise<boolean> {
    const existing = this.findByEntity(entity);
    if (existing?._id) {
      await this.unpinById(existing._id);
      return false;
    }
    await this.pinEntity(entity, name);
    return true;
  }
}
